import { ChangeEvent, FC, KeyboardEvent, useRef, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications';
import styled from '@mint-lib/styled';

import {
  Box,
  IconButton,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from '@myn/mui';

import useBuyingPlanNameMutation from '../../../hooks/useBuyingPlanNameMutation.js';
import { useUpdateBuyingPlanInfoCache } from '../../../hooks/useGetBuyingPlanData.js';
import { BuyingPlanInfo } from '../../../types/buyingPlanInfo.js';

type Props = {
  selectedPlan: BuyingPlanInfo | null;
  editingDisabled: boolean;
};

const MediaPlanName: FC<Props> = ({ selectedPlan, editingDisabled }) => {
  const { t } = useTranslation('@myn/planning');

  const [isPopoverOpened, setPopoverOpened] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState<string | undefined>(selectedPlan?.name);
  const [isError, setIsError] = useState<boolean>(false);
  const updateMediaPlanName = useBuyingPlanNameMutation();
  const updateNameInCache = useUpdateBuyingPlanInfoCache();

  const handlePopoverOpen = () => {
    setPopoverOpened(true);
    setName(selectedPlan?.name);
  };

  const handlePopoverClose = () => {
    setPopoverOpened(false);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };

  const handleInputClear = () => {
    setName('');
    inputRef.current?.focus();
  };

  const showSuccessNotification = () => {
    enqueueSnackbar({
      variant: 'success',
      title: t('Success'),
      subTitle: t('Buying Plan was renamed successfully'),
      autoHideDuration: 3000,
      textLayout: 'inline',
    });
  };

  const showErrorNotification = () => {
    enqueueSnackbar({
      variant: 'error',
      title: t('Error'),
      subTitle: t('Buying Plan was not renamed'),
      autoHideDuration: 3000,
      textLayout: 'inline',
    });
  };

  const handleSaveName = async () => {
    const newName = inputRef.current?.value;
    const skipSavingName =
      !selectedPlan || !newName || selectedPlan.name === newName;

    if (skipSavingName) {
      handlePopoverClose();
      return;
    }

    handlePopoverClose();

    try {
      await updateMediaPlanName({
        buyingPlanId: selectedPlan.id,
        buyingPlanName: newName,
      });
      updateNameInCache({ name: newName });
      showSuccessNotification();
    } catch {
      showErrorNotification();
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && e.target === inputRef.current) {
      handleSubmit();
    }
  };

  const handleSubmit = () => {
    if (!inputRef.current?.value) {
      inputRef.current?.focus();
      setIsError(true);
      return;
    }

    handleSaveName();
    setIsError(false);
  };

  return (
    <Wrapper ref={wrapperRef}>
      <Typography variant="h6">{selectedPlan?.name}</Typography>
      <Tooltip title={t('Rename')}>
        <IconButton
          aria-label="Rename plan"
          data-testid="rename-media-plan"
          variant="icon"
          color="primary"
          size="small"
          icon="Edit"
          onClick={handlePopoverOpen}
          disabled={isPopoverOpened || editingDisabled}
        />
      </Tooltip>
      <Popover
        open={isPopoverOpened}
        onClose={handleSubmit}
        anchorEl={wrapperRef.current}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <PopoverWrapper>
          <TextField
            aria-label="Plan name input"
            value={name}
            fullWidth
            inputRef={inputRef}
            autoFocus
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            error={isError}
            helperText={isError ? t("Can't be blank") : null}
            InputProps={{
              endAdornment: !isError ? (
                <IconButton
                  color="primary"
                  size="small"
                  variant="icon"
                  icon="Close"
                  onClick={handleInputClear}
                />
              ) : null,
            }}
          />
        </PopoverWrapper>
      </Popover>
    </Wrapper>
  );
};

const Wrapper = styled(Box)`
  display: flex;
  align-items: center;
  column-gap: ${({ theme }) => theme.spacing(2)};
  min-height: 36px;
`;

const PopoverWrapper = styled(Box)`
  padding: ${({ theme }) => theme.spacing(0.5)};
  width: 260px;
`;

export default MediaPlanName;
